/** Models for mission 1's depot: the three targets, the alarm post, and generic wreckage. */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';

/** Ammo shack: a store hut with stacked crates outside and a hazard band. Lootable, then a target. */
export const ammoShack: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const wall = p.biome === 'desert' ? 'concrete' : 'timber';
  mb.box(4.4, 2.4, 3.6, { all: p.color(`${wall}.mid`), left: p.color(`${wall}.lo`), right: p.color(`${wall}.lo`) });
  mb.box(4.9, 0.32, 4.1, { all: p.color('roof.1'), top: p.color('roof.0') }, place(0, 2.4, 0));
  mb.box(4.42, 0.3, 3.62, p.color('enemyAccent.0'), place(0, 1.5, 0));
  mb.box(1.5, 1.9, 0.12, p.color('ink.1'), place(0, 0, 1.82));
  mb.box(1.0, 0.7, 1.0, { all: p.color('timber.mid'), top: p.color('timber.hi') }, place(-1.5, 0, 2.6));
  mb.box(1.0, 0.7, 1.0, { all: p.color('timber.mid'), top: p.color('timber.hi') }, place(1.6, 0, 2.5));
  mb.box(0.9, 0.6, 0.9, { all: p.color('timber.lo'), top: p.color('timber.mid') }, place(1.5, 0.7, 2.55));
  return mb;
};

/** Explosives magazine: earth-banked concrete with a blast door. Only a LAW or a bomb opens it. */
export const magazine: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  // Sloped earth berm on three sides sells "bunker-like" from above.
  mb.box(6.8, 0.9, 5.6, { all: p.color('dirt.mid'), top: p.color('ground.1') }, place(0, 0, -0.2));
  mb.box(5.6, 1.9, 4.4, { all: p.color('concrete.mid'), top: p.color('concrete.lo') });
  mb.box(6.0, 0.4, 4.8, { all: p.color('concrete.hi'), top: p.color('concrete.mid') }, place(0, 1.9, 0));
  mb.box(1.9, 1.5, 0.16, p.color('metal.lo'), place(0, 0, 2.22));
  mb.box(1.9, 0.18, 0.2, p.color('fire.2'), place(0, 1.0, 2.26));
  mb.box(0.5, 0.5, 0.5, p.color('metal.mid'), place(-1.8, 2.3, -1.2));
  mb.box(0.5, 0.5, 0.5, p.color('metal.mid'), place(1.8, 2.3, -1.2));
  return mb;
};

/** Alarm post: a pole with a siren horn. A runner who reaches it raises the full alarm. */
export const alarmPost: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.5, 0.3, 0.5, p.color('concrete.lo'));
  mb.cylinder(0.09, 0.07, 2.6, 5, p.color('timber.lo'), place(0, 0.3, 0));
  mb.cylinder(0.12, 0.34, 0.5, 8, p.color('enemyAccent.0'), place(0, 2.75, 0.1, 0, 1, Math.PI / 2), p.color('ink.1'));
  mb.box(0.3, 0.3, 0.2, p.color('metal.mid'), place(0, 1.4, 0.12));
  return mb;
};

/** What is left of a destroyed structure: scorched slabs and beams over its old footprint. */
export function wreck(p: Palette, rng: Pcg32, halfX: number, halfZ: number): MeshBuilder {
  const mb = new MeshBuilder();
  const pieces = Math.max(4, Math.round(halfX * halfZ * 1.6));
  for (let i = 0; i < pieces; i++) {
    const w = rng.range(0.5, 1.5);
    const d = rng.range(0.4, 1.2);
    const tone = rng.chance(0.5) ? 'ink.1' : rng.chance(0.5) ? 'concrete.lo' : 'timber.lo';
    mb.box(w, rng.range(0.15, 0.55), d, p.color(tone), place(rng.range(-halfX, halfX), 0, rng.range(-halfZ, halfZ), rng.range(0, 6.28), 1, rng.range(-0.25, 0.25)));
  }
  // A couple of beams still standing, for a silhouette.
  for (let i = 0; i < 2; i++) {
    mb.box(0.16, rng.range(0.9, 1.7), 0.16, p.color('ink.1'), place(rng.range(-halfX, halfX) * 0.8, 0, rng.range(-halfZ, halfZ) * 0.8, 0, 1, rng.range(-0.3, 0.3)));
  }
  return mb;
}
